import React from 'react';

import '../style.css';
import { Bar } from '@vx/shape';
import { scaleLinear } from '@vx/scale';
import { Text } from '@vx/text';
import LearnMoreButton from '../Buttons/LearnMore';

import CONF from '../Conf';

export default function DailyAverageCard({
  averageScoreToday,
  averageScoreYesterday,
  averageTempToday,
  averageTempYesterday,
  averageHumidToday,
  averageHumidYesterday,
  averageCo2Today,
  averageCo2Yesterday,
  averageVocToday,
  averageVocYesterday,
  averageDustToday,
  averageDustYesterday,
  averagePm25Today,
  averagePm25Yesterday,
  averagePm10Today,
  averagePm10Yesterday,
  abTest,
}) {
  let valueToday, valueYesterday, comp;
  switch (true) {
    case averageScoreToday != null && averageScoreYesterday != null:
      valueToday = Math.round(averageScoreToday);
      valueYesterday = Math.round(averageScoreYesterday);
      comp = 'SCORE';
      break;
    case averageTempToday != null && averageTempYesterday != null:
      valueToday = Math.round(CONF.temperature(averageTempToday));
      valueYesterday = Math.round(CONF.temperature(averageTempYesterday));
      comp = 'TEMP';
      break;
    case averageHumidToday != null && averageHumidYesterday != null:
      valueToday = Math.round(averageHumidToday);
      valueYesterday = Math.round(averageHumidYesterday);
      comp = 'HUMID';
      break;
    case averageCo2Today != null && averageCo2Yesterday != null:
      valueToday = Math.round(averageCo2Today);
      valueYesterday = Math.round(averageCo2Yesterday);
      comp = 'CO2';
      break;
    case averageVocToday != null && averageVocYesterday != null:
      valueToday = Math.round(averageVocToday);
      valueYesterday = Math.round(averageVocYesterday);
      comp = 'VOC';
      break;
    case averageDustToday != null && averageDustYesterday != null:
      valueToday = Math.round(averageDustToday);
      valueYesterday = Math.round(averageDustYesterday);
      comp = 'DUST';
      break;
    case averagePm25Today != null && averagePm25Yesterday != null:
      valueToday = Math.round(averagePm25Today);
      valueYesterday = Math.round(averagePm25Yesterday);
      comp = 'PM25';
      break;
    case averagePm10Today != null && averagePm10Yesterday != null:
      valueToday = Math.round(averagePm10Today);
      valueYesterday = Math.round(averagePm10Yesterday);
      comp = 'PM10';
      break;
    default:
      return null;
  }

  const differenceInValue = Math.abs(valueToday - valueYesterday);
  let body;
  const kind = CONF.formattedDimensionKind(comp);
  const plurality = CONF.formattedDimensionKindPlurality(comp);
  if (valueToday > valueYesterday) {
    body = (
      <p>
        {CONF.text['So far, your average (a) today (b) (c) (d) higher than yesterday'](
          kind,
          plurality,
          differenceInValue,
          CONF.dimensionUnitFromKind(comp, differenceInValue > 1)
        )}
      </p>
    );
  } else if (valueToday < valueYesterday) {
    body = (
      <p>
        {CONF.text['So far, your average (a) today (b) (c) (d) lower than yesterday'](
          kind,
          plurality,
          differenceInValue,
          CONF.dimensionUnitFromKind(comp, differenceInValue > 1)
        )}
      </p>
    );
  } else {
    body = <p>{CONF.text['So far, your average (a) today (b) the same as yesterday'](kind, plurality)}</p>;
  }

  const viewBox = {
    width: 242,
    height: 113,
  };

  const bar = {
    margin: {
      right: 40,
    },
    height: 20,
  };

  const barScale = scaleLinear({
    range: [0, viewBox.width - bar.margin.right],
    domain: [0, Math.max(valueToday, valueYesterday)],
  });

  return (
    <article className='DailyAverage Card' id={abTest}>
      <div>
        <header>
          {CONF.iconFromMetric(comp)}
          <h1>{CONF.formattedDimensionKind(comp)}</h1>
        </header>
        <section className='DailyAverage'>
          {body}
          <svg viewBox={`0 0 ${viewBox.width} ${viewBox.height}`} preserveAspectRatio='xMinYMin meet'>
            <Text
              x={0}
              y={5}
              lineHeight={20}
              textAnchor={'start'}
              verticalAnchor={'start'}
              fontFamily={
                "'SFProDisplay-Light', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI, Roboto, 'Helvetica Neue, 'Helvetica, Arial, sans-serif"
              }
              fontSize={12}
              fill={'var(--black)'}>
              {CONF.text['Today']()}
            </Text>
            <Bar
              width={barScale(valueToday)}
              height={bar.height}
              x={0}
              y={24}
              fill='var(--cool-blue)'
              stroke='transparent'
              strokeWidth='0'
            />
            <Text
              x={barScale(valueToday) + 4}
              y={34}
              lineHeight={20}
              textAnchor={'start'}
              verticalAnchor={'middle'}
              fontFamily={
                "'SFProDisplay-Medium', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI, Roboto, 'Helvetica Neue, 'Helvetica, Arial, sans-serif"
              }
              fontSize={12}
              fill={'var(--black)'}>
              {valueToday}
            </Text>
            <Text
              x={0}
              y={59}
              lineHeight={20}
              textAnchor={'start'}
              verticalAnchor={'start'}
              fontFamily={
                "'SFProDisplay-Light', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI, Roboto, 'Helvetica Neue, 'Helvetica, Arial, sans-serif"
              }
              fontSize={12}
              fill={'var(--black)'}>
              {CONF.text['Yesterday']()}
            </Text>
            <Bar
              width={barScale(valueYesterday)}
              height={bar.height}
              x={0}
              y={78}
              fill='var(--nickel)'
              stroke='transparent'
              strokeWidth='0'
            />
            <Text
              x={barScale(valueYesterday) + 4}
              y={88}
              lineHeight={20}
              textAnchor={'start'}
              verticalAnchor={'middle'}
              fontFamily={
                "'SFProDisplay-Medium', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI, Roboto, 'Helvetica Neue, 'Helvetica, Arial, sans-serif"
              }
              fontSize={12}
              fill={'var(--black)'}>
              {valueYesterday}
            </Text>
          </svg>
        </section>
      </div>
      <footer>
        {/* <LearnMoreButton
          redirectLinkOnClick={`awair://trend?deviceType=${CONF.deviceType}&deviceId=${CONF.deviceId}&component=${CONF.deepLinkComponentNameFromDimensionKind(comp)}&timestamp=${CONF.queryParam.at}`}
          redirectType={"deepLink"}
          language={CONF.queryParam.language}
          fillColor={'var(--cool-blue)'}/> */}
      </footer>
    </article>
  );
}
